import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, Users, Star, Check, X, ArrowLeft, Plus, Minus, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { api, useAuth } from '../App';

const TourDetailPage = () => {
  const { tourId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [selectedAddons] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [specialRequests, setSpecialRequests] = useState('');
  const [isCruiseGuest, setIsCruiseGuest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const response = await api.get(`/tours/${tourId}`);
        setTour(response.data);
      } catch (error) {
        console.error('Error fetching tour:', error);
        toast.error('Tour not found');
        navigate('/tours');
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [tourId, navigate]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedDate || !tour) return;
      
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const response = await api.get(`/tours/${tourId}/availability?date=${dateStr}`);
        setAvailability(response.data);
        setSelectedTime(null);
      } catch (error) {
        console.error('Error fetching availability:', error);
      }
    };
    fetchAvailability();
  }, [selectedDate, tourId, tour]);

  const calculateTotal = () => {
    if (!tour) return { total: 0 };
    
    const adultPrice = tour.base_price;
    const childPrice = tour.child_price || tour.base_price;
    const baseTotal = (adultPrice * adults) + (childPrice * children);
    const addonsTotal = selectedAddons.reduce((sum, addon) => sum + addon.price * addon.quantity, 0);
    const total = baseTotal + addonsTotal;
    
    return { total, adultPrice, childPrice };
  };

  const nextImage = () => {
    if (tour?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % tour.images.length);
    }
  };

  const prevImage = () => {
    if (tour?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + tour.images.length) % tour.images.length);
    }
  };

  const handleBookNow = async () => {
    if (!user) {
      toast.error('Please login to book');
      navigate('/login', { state: { from: `/tours/${tourId}` } });
      return;
    }

    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    const slot = availability?.slots?.find(s => s.time === selectedTime);
    if (!slot || slot.available < (adults + children)) {
      toast.error('Not enough availability for this time slot');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/bookings', {
        tour_id: tourId,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime,
        adults,
        children,
        add_ons: selectedAddons,
        special_requests: specialRequests || null,
        is_cruise_guest: isCruiseGuest
      });

      toast.success('Booking created! Proceed to payment.');
      navigate(`/checkout/${response.data.id}`);
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!tour) return null;

  const { total, adultPrice, childPrice } = calculateTotal();
  const hasChildPricing = tour.child_price && tour.child_price !== tour.base_price;

  return (
    <div className="min-h-screen bg-background pb-32 lg:pb-8">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <Link to="/tours" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Tour
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery */}
            <div className="relative h-64 md:h-[450px] rounded-2xl overflow-hidden group">
              <img 
                src={tour.images?.[currentImageIndex] || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1200'}
                alt={tour.title}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
              
              {/* Gallery Navigation */}
              {tour.images?.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                  
                  {/* Image indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {tour.images.slice(0, 8).map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          currentImageIndex === idx ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'
                        }`}
                        aria-label={`Go to image ${idx + 1}`}
                      />
                    ))}
                    {tour.images.length > 8 && (
                      <span className="text-white text-xs">+{tour.images.length - 8}</span>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {tour.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {tour.images.slice(0, 8).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      currentImageIndex === idx ? 'border-primary' : 'border-transparent hover:border-primary/50'
                    }`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Tour Info */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-teal-light text-primary text-sm font-medium px-3 py-1 rounded-full capitalize">
                  {tour.category.replace('_', ' ')}
                </span>
              </div>

              <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="tour-title">
                {tour.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-6">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-5 h-5" />
                  {tour.meeting_point}, {tour.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-5 h-5" />
                  {tour.duration_hours} hours
                </span>
              </div>

              <div className="prose prose-gray max-w-none">
                {tour.description.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-muted-foreground leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* What's Included */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                <h3 className="font-serif font-bold text-lg mb-4 flex items-center gap-2 text-green-700">
                  <Check className="w-5 h-5" />
                  What's Included
                </h3>
                <ul className="space-y-2">
                  {tour.included.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-green-700">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="font-serif font-bold text-lg mb-4 flex items-center gap-2 text-red-700">
                  <X className="w-5 h-5" />
                  Not Included
                </h3>
                <ul className="space-y-2">
                  {tour.not_included.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-red-700">
                      <X className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Itinerary */}
            {tour.itinerary && tour.itinerary.length > 0 && (
              <div className="bg-gradient-to-br from-[#0a0a0a] to-[#1a1a2e] rounded-2xl p-6 md:p-8">
                <h3 className="font-serif font-bold text-2xl mb-6 text-white flex items-center gap-3">
                  <Clock className="w-6 h-6 text-[#00bcd4]" />
                  Your Day at The Village
                </h3>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[18px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-[#00bcd4] via-[#ffeb3b] to-[#ff6b6b]" />
                  
                  <div className="space-y-6">
                    {tour.itinerary.map((item, idx) => (
                      <div key={idx} className="flex gap-4 relative">
                        {/* Timeline dot */}
                        <div className="w-10 h-10 rounded-full bg-[#00bcd4]/20 border-2 border-[#00bcd4] flex items-center justify-center flex-shrink-0 z-10">
                          <span className="text-[#00bcd4] text-xs font-bold">{idx + 1}</span>
                        </div>
                        
                        <div className="flex-1 pb-2">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-[#00bcd4] font-semibold text-sm">{item.time}</span>
                            <span className="text-white/50">•</span>
                            <h4 className="text-white font-bold">{item.title}</h4>
                          </div>
                          <p className="text-white/70 text-sm">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-border p-6 sticky top-24" data-testid="booking-widget">
              {/* Pricing Display */}
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-primary">BSD ${tour.base_price}</span>
                  <span className="text-muted-foreground">/adult</span>
                </div>
                {hasChildPricing && (
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-xl font-semibold text-primary/80">BSD ${tour.child_price}</span>
                    <span className="text-muted-foreground text-sm">/child (3-10 years)</span>
                  </div>
                )}
              </div>

              {/* Date Picker */}
              <div className="mb-6">
                <Label className="block mb-2 font-semibold">Select Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal h-12"
                      data-testid="date-picker-btn"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Slots */}
              {selectedDate && availability && (
                <div className="mb-6">
                  <Label className="block mb-2 font-semibold">Select Time</Label>
                  {!availability.available ? (
                    <p className="text-sm text-red-500">{availability.reason}</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {availability.slots.map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => setSelectedTime(slot.time)}
                          disabled={slot.available < 1}
                          className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                            selectedTime === slot.time
                              ? 'bg-primary text-white border-primary'
                              : slot.available < 1
                              ? 'bg-muted text-muted-foreground border-border cursor-not-allowed'
                              : 'bg-white hover:border-primary text-foreground border-border'
                          }`}
                          data-testid={`time-slot-${slot.time}`}
                        >
                          {slot.time}
                          <span className="block text-xs opacity-75">
                            {slot.available} spots left
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Participants */}
              <div className="mb-6 space-y-4">
                <Label className="block font-semibold">Participants</Label>
                
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-foreground">Adults</span>
                    <span className="text-muted-foreground text-sm ml-2">BSD ${adultPrice} each</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setAdults(Math.max(1, adults - 1))}
                      disabled={adults <= 1}
                      data-testid="adults-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-semibold" data-testid="adults-count">{adults}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setAdults(adults + 1)}
                      data-testid="adults-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-foreground">Children</span>
                    <span className="text-muted-foreground text-sm ml-2">
                      {hasChildPricing ? `BSD $${childPrice} each (3-10 yrs)` : `BSD $${childPrice} each`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setChildren(Math.max(0, children - 1))}
                      disabled={children <= 0}
                      data-testid="children-minus"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-semibold" data-testid="children-count">{children}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setChildren(children + 1)}
                      data-testid="children-plus"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cruise Guest Toggle */}
              <div className="flex items-center justify-between mb-6 p-3 bg-muted rounded-lg">
                <Label htmlFor="cruise-guest" className="text-sm cursor-pointer">
                  I'm arriving by cruise ship
                </Label>
                <Switch
                  id="cruise-guest"
                  checked={isCruiseGuest}
                  onCheckedChange={setIsCruiseGuest}
                  data-testid="cruise-guest-toggle"
                />
              </div>

              {/* Special Requests */}
              <div className="mb-6">
                <Label className="block mb-2 font-semibold">Special Requests</Label>
                <Textarea
                  placeholder="Dietary needs, accessibility, celebrations..."
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  className="h-20"
                  data-testid="special-requests"
                />
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-border pt-4 mb-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{adults} Adult{adults > 1 ? 's' : ''} × ${adultPrice}</span>
                  <span>${(adults * adultPrice).toFixed(2)}</span>
                </div>
                {children > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{children} Child{children > 1 ? 'ren' : ''} × ${childPrice}</span>
                    <span>${(children * childPrice).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-2xl text-primary">BSD ${total.toFixed(2)}</span>
                </div>
              </div>

              <Button
                className="w-full btn-primary text-lg py-6"
                onClick={handleBookNow}
                disabled={submitting || !selectedDate || !selectedTime}
                data-testid="book-now-btn"
              >
                {submitting ? 'Creating Booking...' : 'Book Now'}
              </Button>

              <p className="text-center text-sm text-muted-foreground mt-4">
                See our <Link to="/cancellation-policy" className="text-primary hover:underline">Cancellation Policy</Link> for details
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 z-50">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Total</p>
            <p className="text-xl font-bold text-primary">BSD ${total.toFixed(2)}</p>
          </div>
          <Button
            className="btn-primary px-8"
            onClick={handleBookNow}
            disabled={submitting || !selectedDate || !selectedTime}
            data-testid="book-now-btn-mobile"
          >
            {submitting ? 'Booking...' : 'Book Now'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TourDetailPage;
